<template>
  <NavBar v-if="userLoaded" pageName="Reports"></NavBar>

  <v-container style="margin-top: 185px;">
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Header with refresh button -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="6">
        <h2>{{ t.reports.title }}</h2>
        <p class="text-subtitle-2 text-grey">
          {{ t.reports.subclients }}: {{ subclientCount }}
        </p>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end">
        <v-btn color="primary" variant="outlined" :loading="loading" @click="loadReports">
          <v-icon class="mr-2">mdi-refresh</v-icon>
          {{ t.common.reset }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Section 1: Available Services -->
    <v-card class="mb-4">
      <v-card-title class="bg-primary text-white">
        <v-icon class="mr-2">mdi-server</v-icon>
        {{ t.reports.available_services }}
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="availableServicesHeaders"
          :items="availableServicesData"
          density="compact"
          class="elevation-1"
          :items-per-page="-1"
        >
          <template v-slot:bottom>
            <v-divider></v-divider>
            <v-table density="compact">
              <tbody>
                <tr class="font-weight-bold bg-grey-lighten-3">
                  <td>{{ t.common.total }}</td>
                  <td>{{ availableServicesTotals.client }}</td>
                  <td>{{ availableServicesTotals.msisdn }}</td>
                  <td>{{ availableServicesTotals.imsi }}</td>
                  <td>{{ availableServicesTotals.imsi_msisdn }}</td>
                  <td>{{ availableServicesTotals.total }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Section 2: Allocated Services -->
    <v-card class="mb-4">
      <v-card-title class="bg-success text-white">
        <v-icon class="mr-2">mdi-account-check</v-icon>
        {{ t.reports.allocated_services }}
      </v-card-title>
      <v-card-text>
        <v-tabs v-model="allocatedTab" color="success">
          <v-tab value="own">{{ t.reports.own_client }}</v-tab>
          <v-tab value="subclients">{{ t.reports.subclients }} ({{ subclientCount }})</v-tab>
        </v-tabs>
        <v-window v-model="allocatedTab">
          <v-window-item value="own">
            <v-data-table
              :headers="allocatedServicesHeaders"
              :items="allocatedServicesOwn"
              density="compact"
              class="mt-2"
              :items-per-page="-1"
            >
              <template v-slot:bottom>
                <v-divider></v-divider>
                <v-table density="compact">
                  <tbody>
                    <tr class="font-weight-bold bg-grey-lighten-3">
                      <td>{{ t.common.total }}</td>
                      <td>{{ allocatedOwnTotals.active }}</td>
                      <td>{{ allocatedOwnTotals.expired }}</td>
                      <td>{{ allocatedOwnTotals.total }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
            </v-data-table>
          </v-window-item>
          <v-window-item value="subclients">
            <v-data-table
              :headers="allocatedServicesHeaders"
              :items="allocatedServicesSubclients"
              density="compact"
              class="mt-2"
              :items-per-page="-1"
            >
              <template v-slot:bottom>
                <v-divider></v-divider>
                <v-table density="compact">
                  <tbody>
                    <tr class="font-weight-bold bg-grey-lighten-3">
                      <td>{{ t.common.total }}</td>
                      <td>{{ allocatedSubclientsTotals.active }}</td>
                      <td>{{ allocatedSubclientsTotals.expired }}</td>
                      <td>{{ allocatedSubclientsTotals.total }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
            </v-data-table>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>

    <!-- Section 3: Revenue -->
    <v-card class="mb-4">
      <v-card-title class="bg-warning">
        <v-icon class="mr-2">mdi-currency-usd</v-icon>
        {{ t.reports.revenue }}
      </v-card-title>
      <v-card-text>
        <!-- Revenue Collected -->
        <h4 class="mb-2 mt-2">{{ t.reports.revenue_collected }}</h4>
        <v-data-table
          :headers="revenueCollectedHeaders"
          :items="revenueCollectedData"
          density="compact"
          class="mb-4 elevation-1"
          :items-per-page="-1"
        >
          <template v-slot:bottom>
            <v-divider></v-divider>
            <v-table density="compact">
              <tbody>
                <tr class="font-weight-bold bg-grey-lighten-3">
                  <td>{{ t.common.total }}</td>
                  <td>{{ revenueCollectedTotals.this_year }}</td>
                  <td>{{ revenueCollectedTotals.last_month }}</td>
                  <td>{{ revenueCollectedTotals.current_month }}</td>
                  <td>{{ revenueCollectedTotals.yesterday }}</td>
                  <td>{{ revenueCollectedTotals.today }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </v-data-table>

        <!-- Revenue To Be Billed -->
        <h4 class="mb-2">{{ t.reports.revenue_to_be_billed }}</h4>
        <v-data-table
          :headers="revenueToBeBilledHeaders"
          :items="revenueToBeBilledData"
          density="compact"
          class="elevation-1"
          :items-per-page="-1"
        >
          <template v-slot:bottom>
            <v-divider></v-divider>
            <v-table density="compact">
              <tbody>
                <tr class="font-weight-bold bg-grey-lighten-3">
                  <td>{{ t.common.total }}</td>
                  <td>{{ revenueToBeBilledTotals.end_of_month }}</td>
                  <td>{{ revenueToBeBilledTotals.next_30_days }}</td>
                  <td>{{ revenueToBeBilledTotals.next_90_days }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Section 4: Expirations -->
    <v-card class="mb-4">
      <v-card-title class="bg-error text-white">
        <v-icon class="mr-2">mdi-calendar-alert</v-icon>
        {{ t.reports.expirations }}
      </v-card-title>
      <v-card-text>
        <v-tabs v-model="expirationsTab" color="error">
          <v-tab value="own">{{ t.reports.own_client }}</v-tab>
          <v-tab value="subclients">{{ t.reports.subclients }} ({{ subclientCount }})</v-tab>
        </v-tabs>
        <v-window v-model="expirationsTab">
          <v-window-item value="own">
            <v-data-table
              :headers="expirationsHeaders"
              :items="expirationsOwn"
              density="compact"
              class="mt-2"
              :items-per-page="-1"
            >
              <template v-slot:bottom>
                <v-divider></v-divider>
                <v-table density="compact">
                  <tbody>
                    <tr class="font-weight-bold bg-grey-lighten-3">
                      <td>{{ t.common.total }}</td>
                      <td>{{ expirationsOwnTotals.expires_1_day }}</td>
                      <td>{{ expirationsOwnTotals.expires_3_days }}</td>
                      <td>{{ expirationsOwnTotals.expires_7_days }}</td>
                      <td>{{ expirationsOwnTotals.expires_30_days }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
            </v-data-table>
          </v-window-item>
          <v-window-item value="subclients">
            <v-data-table
              :headers="expirationsHeaders"
              :items="expirationsSubclients"
              density="compact"
              class="mt-2"
              :items-per-page="-1"
            >
              <template v-slot:bottom>
                <v-divider></v-divider>
                <v-table density="compact">
                  <tbody>
                    <tr class="font-weight-bold bg-grey-lighten-3">
                      <td>{{ t.common.total }}</td>
                      <td>{{ expirationsSubclientsTotals.expires_1_day }}</td>
                      <td>{{ expirationsSubclientsTotals.expires_3_days }}</td>
                      <td>{{ expirationsSubclientsTotals.expires_7_days }}</td>
                      <td>{{ expirationsSubclientsTotals.expires_30_days }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
            </v-data-table>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { getReportsData } from "@/composables/useGetReportsAPI";
import { log } from "@/composables/useLog";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'ReportsView',
  components: { NavBar },
  data() {
    return {
      loading: false,
      token: '',
      userLoaded: false,
      subclientCount: 0,

      // Available Services
      availableServicesData: [],
      availableServicesTotals: { client: 0, msisdn: 0, imsi: 0, imsi_msisdn: 0, total: 0 },

      // Allocated Services
      allocatedTab: 'own',
      allocatedServicesOwn: [],
      allocatedServicesSubclients: [],

      // Revenue
      revenueCollectedData: [],
      revenueToBeBilledData: [],

      // Expirations
      expirationsTab: 'own',
      expirationsOwn: [],
      expirationsSubclients: [],
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    availableServicesHeaders() {
      return [
        { title: this.t.reports?.service_name || 'Service Name', key: 'service_name', width: '30%' },
        { title: this.t.reports?.client_type || 'Client', key: 'client_count', align: 'center' },
        { title: this.t.reports?.msisdn_type || 'MSISDN', key: 'msisdn_count', align: 'center' },
        { title: this.t.reports?.imsi_type || 'IMSI', key: 'imsi_count', align: 'center' },
        { title: this.t.reports?.imsi_msisdn_type || 'IMSI+MSISDN', key: 'imsi_msisdn_count', align: 'center' },
        { title: this.t.common?.total || 'Total', key: 'total', align: 'center' },
      ];
    },
    allocatedServicesHeaders() {
      return [
        { title: this.t.reports?.service_name || 'Service Name', key: 'service_name', width: '40%' },
        { title: this.t.reports?.active_count || 'Active', key: 'active_count', align: 'center' },
        { title: this.t.reports?.expired_count || 'Expired', key: 'expired_count', align: 'center' },
        { title: this.t.common?.total || 'Total', key: 'total', align: 'center' },
      ];
    },
    revenueCollectedHeaders() {
      return [
        { title: this.t.reports?.service_name || 'Service Name', key: 'service_name', width: '25%' },
        { title: this.t.reports?.this_year || 'This Year', key: 'this_year', align: 'end' },
        { title: this.t.reports?.last_month || 'Last Month', key: 'last_month', align: 'end' },
        { title: this.t.reports?.current_month || 'Current Month', key: 'current_month', align: 'end' },
        { title: this.t.reports?.yesterday || 'Yesterday', key: 'yesterday', align: 'end' },
        { title: this.t.reports?.today || 'Today', key: 'today', align: 'end' },
      ];
    },
    revenueToBeBilledHeaders() {
      return [
        { title: this.t.reports?.service_name || 'Service Name', key: 'service_name', width: '40%' },
        { title: this.t.reports?.end_of_month || 'End of Month', key: 'end_of_month', align: 'end' },
        { title: this.t.reports?.next_30_days || 'Next 30 Days', key: 'next_30_days', align: 'end' },
        { title: this.t.reports?.next_90_days || 'Next 90 Days', key: 'next_90_days', align: 'end' },
      ];
    },
    expirationsHeaders() {
      return [
        { title: this.t.reports?.service_name || 'Service Name', key: 'service_name', width: '40%' },
        { title: this.t.reports?.expires_1_day || '1 Day', key: 'expires_1_day', align: 'center' },
        { title: this.t.reports?.expires_3_days || '3 Days', key: 'expires_3_days', align: 'center' },
        { title: this.t.reports?.expires_7_days || '7 Days', key: 'expires_7_days', align: 'center' },
        { title: this.t.reports?.expires_30_days || '30 Days', key: 'expires_30_days', align: 'center' },
      ];
    },
    // Totals computed properties
    allocatedOwnTotals() {
      return this.computeTotals(this.allocatedServicesOwn, ['active_count', 'expired_count', 'total']);
    },
    allocatedSubclientsTotals() {
      return this.computeTotals(this.allocatedServicesSubclients, ['active_count', 'expired_count', 'total']);
    },
    revenueCollectedTotals() {
      return this.computeTotals(this.revenueCollectedData, ['this_year', 'last_month', 'current_month', 'yesterday', 'today']);
    },
    revenueToBeBilledTotals() {
      return this.computeTotals(this.revenueToBeBilledData, ['end_of_month', 'next_30_days', 'next_90_days']);
    },
    expirationsOwnTotals() {
      return this.computeTotals(this.expirationsOwn, ['expires_1_day', 'expires_3_days', 'expires_7_days', 'expires_30_days']);
    },
    expirationsSubclientsTotals() {
      return this.computeTotals(this.expirationsSubclients, ['expires_1_day', 'expires_3_days', 'expires_7_days', 'expires_30_days']);
    },
  },
  methods: {
    computeTotals(data, fields) {
      const totals = {};
      fields.forEach(field => {
        totals[field] = data.reduce((sum, item) => sum + (parseFloat(item[field]) || 0), 0);
        // Round to 2 decimals if it's a decimal
        if (totals[field] % 1 !== 0) {
          totals[field] = totals[field].toFixed(2);
        }
      });
      return totals;
    },
    async loadReports() {
      this.loading = true;
      try {
        const result = await getReportsData(this.token);
        log("Reports loaded:")
        log(result)
        if (!result.error) {
          this.subclientCount = result.subclient_count || 0;
          this.availableServicesData = result.available_services?.data || [];
          this.availableServicesTotals = result.available_services?.totals || { client: 0, msisdn: 0, imsi: 0, imsi_msisdn: 0, total: 0 };
          this.allocatedServicesOwn = result.allocated_services?.own || [];
          this.allocatedServicesSubclients = result.allocated_services?.subclients || [];
          this.revenueCollectedData = result.revenue?.collected || [];
          this.revenueToBeBilledData = result.revenue?.to_be_billed || [];
          this.expirationsOwn = result.expirations?.own || [];
          this.expirationsSubclients = result.expirations?.subclients || [];
        } else {
          alert(result.message || "Error loading reports");
        }
      } catch (err) {
        log("Error loading reports:");
        log(err);
        alert("Error loading reports");
      } finally {
        this.loading = false;
      }
    }
  },
  async created() {
    const { getLoggedIn } = useGetLoggedIn();
    await getLoggedIn();
    this.token = store.getters.getToken;
    this.userLoaded = true;
    await this.loadReports();
  }
});
</script>
